import { ChangeDetectionStrategy, Component, inject, Input, OnInit, signal, ViewChild } from "@angular/core";
import {
  AccountSelectionService,
  adStatsGroupBy,
  MetricsSelectorPage,
  SegmentService,
  StatsService,
  UserSelectionService,
} from "@front/m19-services";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";

import { AgGridAngular } from "@ag-grid-community/angular";
import { GridOptions, ICellRendererParams, IRowNode } from "@ag-grid-community/core";
import { AccountMarketplace, CampaignType, Currency, Placement, SegmentType } from "@front/m19-api-client";
import { getFilteredLeafNodes, getGroupRowAgg, gridConfig, SIDE_BAR_NO_PIVOT } from "@front/m19-grid-config";
import {
  ACOS,
  AD_CONVERSIONS,
  AD_SALES,
  CLICK_THROUGH_RATE,
  CLICKS,
  CONVERSION_RATE,
  COST,
  CPC,
  IMPRESSIONS,
  Metric,
  ROAS,
} from "@front/m19-metrics";
import { AdStatsEx, SegmentEx, StrategyEx, SupportedAudienceMatchType } from "@front/m19-models";
import { IGridButtonComponent, ModalOptions, ModalService } from "@front/m19-ui";
import { mergeSeveralDates, sumAdStats } from "@front/m19-utils";
import { TranslocoService } from "@jsverse/transloco";
import { actionColumnProperties, CurrencyColumn } from "@m19-board/grid-config/grid-columns";
import { exportGridCsv, getCsvFileName } from "@m19-board/grid-config/grid-config";
import { GridConfigService } from "@m19-board/grid-config/grid-config.service";
import { StrategyStats, SubStrategyStats } from "@m19-board/models/Metric";
import {
  ChartData,
  ChartModalComponent,
  ChartModalInputData,
} from "@m19-board/shared/chart-modal/chart-modal.component";
import { combineLatest, map, Observable, of, switchMap } from "rxjs";

@UntilDestroy()
@Component({
  selector: "app-strategy-placement-stats-grid",
  template: ` <div class="ag-theme-quartz h-full">
    <ag-grid-angular class="h-full" [gridOptions]="gridOptions" [rowData]="gridData()" rowGroupPanelShow="always" />
  </div>`,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [AgGridAngular],
})
export class StrategyPlacementStatsGridComponent implements OnInit {
  private readonly gridConfigService = inject(GridConfigService);
  private readonly modalService = inject(ModalService);
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly statsService = inject(StatsService);
  private readonly segmentService = inject(SegmentService);
  private readonly userSelectionService = inject(UserSelectionService);
  private readonly translocoService = inject(TranslocoService);

  PlacementDefKeys: { [key in Placement]: string } = {
    [Placement.top]: this.translocoService.translate("strategy-placement-stats-grid.top_of_search"),
    [Placement.searchOnAmazon]: this.translocoService.translate("strategy-placement-stats-grid.search_on-amazon"),
    [Placement.productPage]: this.translocoService.translate("strategy-placement-stats-grid.product_page"),
    [Placement.offAmazon]: this.translocoService.translate("strategy-placement-stats-grid.off-amazon"),
    [Placement.other]: this.translocoService.translate("strategy-placement-stats-grid.other_search"),
  };

  @Input({ required: true }) strategy!: StrategyEx;
  @Input({ required: true }) currency!: Currency;
  @Input({ required: true }) locale!: string;

  accountMarketplace?: AccountMarketplace;

  readonly gridData = signal<SubStrategyStats[] | undefined>(undefined);
  previousDataPlacementMap: Map<string, AdStatsEx> = new Map<string, AdStatsEx>();

  @ViewChild(AgGridAngular) agGrid!: AgGridAngular;

  readonly METRIC_COLUMNS: Metric<StrategyStats>[] = [
    AD_SALES,
    AD_CONVERSIONS,
    COST,
    ACOS,
    CLICKS,
    IMPRESSIONS,
    CLICK_THROUGH_RATE,
    CONVERSION_RATE,
    CPC,
    ROAS,
  ];

  readonly gridOptions: GridOptions = {
    ...gridConfig({
      gridKey: "strategy-placement-stats",
      withFooterRow: true,
      autoSizeDefault: true,
      aggFunction: sumAdStats,
    }),
    getGroupRowAgg: getGroupRowAgg,
    groupDisplayType: "singleColumn",
    defaultColDef: {
      sortable: true,
      filter: true,
      resizable: true,
      useValueFormatterForExport: true,
    },
    columnDefs: [
      {
        headerValueGetter: () =>
          this.strategy.campaignType == CampaignType.SD
            ? this.translocoService.translate("strategy-placement-stats-grid.audience")
            : this.translocoService.translate("sp-strategy-group-page.tactic"),
        field: "subStrategyName",
        pinned: "left",
        rowGroup: true,
        hide: true,
        floatingFilter: true,
        enableRowGroup: true,
      },
      {
        headerName: this.translocoService.translate("strategy-placement-stats-grid.placement", {}, "en"),
        headerValueGetter: () => this.translocoService.translate("strategy-placement-stats-grid.placement"),
        field: "placement",
        pinned: "left",
        hide: true,
        floatingFilter: true,
        enableRowGroup: true,
      },
      ...this.gridConfigService.getMetricsColDef(this.METRIC_COLUMNS, false).map(({ headerName, ...def }) => ({
        ...def,
        cellRendererParams: (params: any) => {
          const prevData: AdStatsEx = {};
          let empty = true;
          if (params.node.childrenAfterFilter) {
            for (const n of params.node.childrenAfterFilter) {
              const key = n.data?.subStrategyId + "-" + n.data?.placement;
              const data = this.previousDataPlacementMap.get(key);
              if (data) {
                sumAdStats(prevData, data);
                empty = false;
              }
            }
          }
          return {
            ...(def as any).cellRendererParams(params),
            previousData: empty ? undefined : prevData,
            currency: this.currency,
            locale: this.locale,
          };
        },
      })),
      {
        ...actionColumnProperties<AdStatsEx, string>(),
        cellRenderer: IGridButtonComponent,
        cellRendererParams: (params: ICellRendererParams<SubStrategyStats>) => ({
          icon: "chartLine",
          clickAction: () => {
            this.openStatsModal(params.node);
          },
        }),
      },
      { ...CurrencyColumn, headerName: this.translocoService.translate("common.currency") },
    ],
    groupLockGroupColumns: 1,
    getContextMenuItems: () => ["copy", "export", "chartRange"],
    sideBar: SIDE_BAR_NO_PIVOT,
    autoGroupColumnDef: {
      headerValueGetter: () =>
        this.strategy.campaignType == CampaignType.SD
          ? this.translocoService.translate("strategy-placement-stats-grid.audience_placement")
          : this.translocoService.translate("strategy-placement-stats-grid.tactic_placement"),
      field: "placement",
      colId: "placementGroup",
      suppressSizeToFit: true,
      maxWidth: 300,
      valueFormatter: (params) => {
        return params.node?.isRowPinned() ? "" : ((this.PlacementDefKeys as any)[params.value] ?? "Unknown placement");
      },
      floatingFilter: true,
      filter: "agGroupColumnFilter",
    },
  };

  ngOnInit(): void {
    localStorage.removeItem("strategyPlacementGrid_gridConfig");
    this.accountSelectionService.singleAccountMarketplaceSelection$
      .pipe(untilDestroyed(this))
      .subscribe((selection) => {
        this.accountMarketplace = selection;
      });

    combineLatest([
      combineLatest([
        this.accountSelectionService.singleAccountMarketplaceSelection$,
        this.userSelectionService.dateRange$,
        this.userSelectionService.selectedCurrency$,
      ]).pipe(
        switchMap(([am, dr, currency]) =>
          this.statsService.getDailyPlacementStats(am.accountId, am.marketplace, currency, dr[0], dr[1]),
        ),
      ),
      this.accountSelectionService.singleAccountMarketplaceSelection$.pipe(
        switchMap((am) => this.segmentService.getSegments(am.accountId, am.marketplace)),
      ),
    ])
      .pipe(untilDestroyed(this))
      .subscribe(([data, segmentIndex]) => {
        const placementData: AdStatsEx[] = Array.from(
          adStatsGroupBy<string>(
            data.filter((d) => d.strategyId == this.strategy.strategyId),
            (x) => x?.strategyId + "-" + x?.subStrategyId + "-" + x?.placement,
          ).values(),
        );
        this.gridData.set(placementData.map((d) => this.buildSubStrategy(d, segmentIndex)));
      });

    combineLatest([
      this.accountSelectionService.singleAccountMarketplaceSelection$,
      this.userSelectionService.periodComparison$,
      this.userSelectionService.selectedCurrency$,
    ])
      .pipe(
        switchMap(([am, pc, currency]) => {
          if (!pc?.period) {
            return of([]);
          }
          return this.statsService.getDailyPlacementStats(
            am.accountId,
            am.marketplace,
            currency,
            pc.period[0],
            pc.period[1],
          );
        }),
        untilDestroyed(this),
      )
      .subscribe((previousData: AdStatsEx[]) => {
        this.previousDataPlacementMap.clear();
        Array.from(
          adStatsGroupBy<string>(
            previousData.filter((d) => d.strategyId == this.strategy.strategyId),
            (d) => d.subStrategyId + "-" + d.placement,
          ).values(),
        ).forEach((d) => {
          this.previousDataPlacementMap.set(d.subStrategyId + "-" + d.placement, d);
        });

        // Have to redraw rows because we need all the row data and not just the cells
        this.agGrid?.api.redrawRows();
      });
  }

  private buildSubStrategy(d: AdStatsEx, segmentIndex: Map<number, SegmentEx>): SubStrategyStats {
    let subStrategyName = this.translocoService.translate("strategy-placement-stats-grid.all_other_traffic");
    if (d.subStrategyId) {
      // for remarketing, the subStrategyId is an audience
      if (d.segmentType === SegmentType.remarketing) {
        const audience =
          this.strategy.isSdStrategy() &&
          this.strategy?.audienceTargetings?.find((a) => a.audienceTargetId === d.subStrategyId);
        if (audience) {
          subStrategyName = `${SupportedAudienceMatchType[audience.matchType!]} last ${audience.lookback} days`;
        } else {
          subStrategyName = "Deleted Audience";
        }
      } else if (this.strategy.isAdvertisingStrategy()) {
        // the subStrategyId is null or a tactic
        const segment = segmentIndex.get(d.subStrategyId);
        const deletedFromStrategy = !this.strategy?.tactics.map((t) => t.segmentId).includes(d.subStrategyId);
        subStrategyName = segment?.name ? segment.name + (deletedFromStrategy ? " (Deleted)" : "") : "Deleted Segment";
      }
    }

    return {
      ...d,
      placement: d.placement,
      strategyName: this.strategy.name!,
      state: this.strategy.state,
      subStrategyName: subStrategyName,
    };
  }

  private openStatsModal(row: IRowNode<SubStrategyStats>) {
    const rowsToProcess: IRowNode<SubStrategyStats>[] = getFilteredLeafNodes(row, this.agGrid?.api);
    const rowData = rowsToProcess.map((r) => ({ id: r.data?.subStrategyId, placement: r.data?.placement }));
    const modalData$: Observable<ChartData<StrategyStats>> = combineLatest([
      this.accountSelectionService.singleAccountMarketplaceSelection$,
      this.userSelectionService.dateRange$,
      this.userSelectionService.periodComparison$,
      this.userSelectionService.selectedCurrency$,
    ]).pipe(
      switchMap(([am, dr, pc, currency]) =>
        combineLatest([
          this.statsService.getDailyPlacementStats(am.accountId, am.marketplace, currency, dr[0], dr[1]),
          pc?.period
            ? this.statsService.getDailyPlacementStats(
                am.accountId,
                am.marketplace,
                currency,
                pc.period[0],
                pc.period[1],
              )
            : of([]),
        ]),
      ),
      map(([data, previousData]) => ({
        allData: data,
        allPreviousData: previousData,
      })),
      map(({ allData, allPreviousData }) => {
        const data: StrategyStats[] = [];
        const totalData: StrategyStats = { strategyName: this.strategy.name! };
        for (const d of allData) {
          if (
            d.strategyId == this.strategy.strategyId &&
            rowData.find((r) => r.id == d.subStrategyId && r.placement == d.placement)
          ) {
            data.push({ ...d, strategyName: this.strategy.name! });
            mergeSeveralDates(totalData, d);
          }
        }
        const previousData: StrategyStats[] = [];
        const totalPreviousData: StrategyStats = { strategyName: this.strategy.name! };
        for (const d of allPreviousData) {
          if (d.subStrategyId && rowData.find((r) => r.id == d.subStrategyId && r.placement == d.placement)) {
            previousData.push({ ...d, strategyName: this.strategy.name! });
            mergeSeveralDates(totalPreviousData, d);
          }
        }
        return { totalData, data, previousData, totalPreviousData };
      }),
    );
    const modalOption: ModalOptions<ChartModalInputData<StrategyStats>> = {
      modalTitle: this.translocoService.translate("strategy-placement-stats-grid.detailed_stats"),
      data: {
        locale: this.locale!,
        currency: this.currency as Currency,
        dataSetConfiguration: {
          aggregationFunction: [mergeSeveralDates],
          metricsOnSameScale: [[AD_SALES, COST]],
        },
        chartData$: modalData$,
        selectedMetrics: [AD_SALES, COST],
        metrics: this.METRIC_COLUMNS,
        localStorageKey: { page: MetricsSelectorPage.tacticPlacementStats },
      },
    };
    this.modalService.openModal<ChartModalInputData<StrategyStats>, void>(ChartModalComponent, modalOption);
  }

  public exportCsv() {
    const suffix =
      this.strategy.campaignType == CampaignType.SD ? "audience_placement_stats" : "tactic_placement_stats";

    const fileName = getCsvFileName(
      this.strategy.name + "_" + suffix,
      this.accountMarketplace!.accountGroupName,
      this.accountMarketplace!.marketplace,
      this.userSelectionService.getDateRangeStr(),
    );
    const columnKeys: string[] = this.agGrid?.api
      .getAllDisplayedColumns()
      .map((c) => c.getColId())
      .concat(["currency"]);

    exportGridCsv(this.agGrid?.api, { fileName, columnKeys, skipColumnGroupHeaders: true });
  }

  restoreDefaultColumns() {
    this.agGrid.api.resetColumnState();
    this.agGrid.api.autoSizeAllColumns();
  }
}
